import type { BalanceType } from '@pp/core/src/api/fetchers/userInfo/types';
import { SessionStorage } from '@pp/core/src/storage/session/SessionStorage';
import { DEFAULT_LANGUAGE_CODE } from '@pp/core/src/utils/language';
import { SESSION_KEYS } from '@pp/core/src/templateScripts/scripts/bundleLoader/types';
import { BaseLauncherCommunicationController } from '@pp/core/src/controllers/launcherCommunication/BaseLauncherCommunicationController';
import type {
  AddGamePayload,
  AddMTBPayload,
  BalanceUpdatePayload,
  MtbBalanceUpdatePayload,
  BaseLauncherPayload,
  BetsPlacedPayload,
  BetInfoPayload,
  GameClosePayload,
  GameLaunchedPayload,
  LauncherPayload,
  TablesUpdatePayload,
  GamesLaunchedPayload,
  RedirectPayload,
  ThemeChangePayload,
  ToggleMtbPayload,
  LauncherGroup,
  LauncherMessages,
  MtbBalanceType,
  BonusCampaignPayload,
  ReplaceTablePayload,
  PopupPayload,
  ToggleNoCommissionPayload,
  ChangeLanguagePayload,
  WinResultPayload,
} from '@pp/core/src/controllers/launcherCommunication/types';
import { LAUNCHER_GROUP, LAUNCHER_MESSAGES } from '@pp/core/src/controllers/launcherCommunication/types';
import { Logger } from '@pp/core/src/logger/Logger';
import type { BetInfo } from '@pp/core/src/store/slices/launcher/types';
import { IFRAME_TYPE } from '@pp/core/src/store/slices/launcher/types';

import { launcherContextActions } from '../context/actions';
import type { LauncherContextType } from '../context/LauncherContext';
import type { LauncherTableInfo } from '../context/types';

import { BetsInfoController } from './BetsInfoController';

const LAUNCHER_ID = 'LAUNCHERID';

/**
 * Host → game iframe relay payloads (integration-specific shapes).
 * - PaddyPower-style: { source: 'gameWindow', type, payload? }
 * - GGNet-style: { event, params? }
 * - Bridge API-style: { Type, Name, ... }
 */
function isHostToGameRelayPayload(msgdata: unknown): boolean {
  if (msgdata === null || typeof msgdata !== 'object' || Array.isArray(msgdata)) {
    return false;
  }
  const d = msgdata as Record<string, unknown>;

  if (d.source === 'gameWindow' && typeof d.type === 'string' && d.type.length > 0) {
    return true;
  }

  if (typeof d.event === 'string' && d.event.length > 0) {
    return true;
  }

  if (typeof d.Type === 'string' && typeof d.Name === 'string' && d.Type.length > 0 && d.Name.length > 0) {
    return true;
  }

  return false;
}

export class LauncherCommunicationController extends BaseLauncherCommunicationController {
  protected store: LauncherContextType | null;

  constructor() {
    super();
    this.store = null;
  }

  setStore(store: LauncherContextType): void {
    this.store = store;
  }

  private createMessage<T>(name: LauncherMessages, group: LauncherGroup, payload?: T): LauncherPayload | null {
    if (!this.store) {
      return null;
    }
    return {
      name,
      group,
      originTableId: LAUNCHER_ID,
      ...(payload && { payload }),
    };
  }

  dispatchTablesUpdatedMessage(storeTables: LauncherTableInfo[], frameId?: string): void {
    const payload: TablesUpdatePayload = {
      tables: storeTables.map((table: LauncherTableInfo) => ({
        tableId: table.tableId,
        iframeType: table.iframeType,
        operatorGameId: table.operatorGameId,
        isMtbMinimized: table.isMtbMinimized,
      })),
    };

    const message = this.createMessage<TablesUpdatePayload>(
      LAUNCHER_MESSAGES.TABLES_UPDATED,
      LAUNCHER_GROUP.LAUNCHER,
      payload
    );

    if (message) this.dispatch(message, frameId);
  }

  dispatchBalanceUpdateMessage(balance: BalanceType, tableId?: string, excludeTableId?: boolean): void {
    const message = this.createMessage<BalanceUpdatePayload>(
      LAUNCHER_MESSAGES.BALANCE_UPDATE,
      LAUNCHER_GROUP.LAUNCHER,
      {
        balance,
      }
    );
    this.dispatch(message, tableId, excludeTableId);
  }

  dispatchBetsUpdatedMessage(betInfo: BetInfo, tableId?: string, excludeTableId?: boolean): void {
    const message = this.createMessage<BetInfoPayload>(LAUNCHER_MESSAGES.BETS_UPDATED, LAUNCHER_GROUP.LAUNCHER, {
      betInfo,
    });

    this.dispatch(message, tableId, excludeTableId);
  }

  dispatchSetActiveTableMessage(tableId: string, toTableId?: string): void {
    const payload: BaseLauncherPayload = {
      tableId,
    };
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.SET_ACTIVE_TABLE,
      group: LAUNCHER_GROUP.LAUNCHER,
      originTableId: LAUNCHER_ID,
      payload,
    };
    this.dispatch(message, toTableId);
  }

  dispatchThemeChangeMessage(theme: string): void {
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.THEME_CHANGE,
      group: LAUNCHER_GROUP.MTB,
      originTableId: LAUNCHER_ID,
      payload: { theme } as ThemeChangePayload,
    };
    this.dispatch(message);
  }

  dispatchComputedBalanceUpdate(balance: MtbBalanceType): void {
    const payload: MtbBalanceUpdatePayload = {
      balance,
    };
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.COMPUTED_BALANCE_UPDATE,
      group: LAUNCHER_GROUP.MTB,
      originTableId: LAUNCHER_ID,
      payload,
    };

    this.dispatch(message);
  }

  dispatchBonusCampaignMessage(payload: BonusCampaignPayload, tableId?: string, excludeTableId?: boolean): void {
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.BONUS_CAMPAIGNS,
      group: LAUNCHER_GROUP.LAUNCHER,
      originTableId: LAUNCHER_ID,
      payload,
    };
    this.dispatch(message, tableId, excludeTableId);
  }

  dispatchShowPopup(popupType: string): void {
    // External message (if needed)
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.SHOW_POPUP,
      group: LAUNCHER_GROUP.LAUNCHER,
      originTableId: LAUNCHER_ID,
      payload: { popupType } as PopupPayload,
    };
    this.dispatch(message);
  }

  dispatchNoCommissionChangeMessage(isNoCommission: boolean): void {
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.TOGGLE_NO_COMMISSION,
      group: LAUNCHER_GROUP.MTB,
      originTableId: LAUNCHER_ID,
      payload: { isNoCommission } as ToggleNoCommissionPayload,
    };
    this.dispatch(message);
  }

  dispatchChangeLanguageMessage(lang: string, tableId?: string): void {
    const message: LauncherPayload = {
      name: LAUNCHER_MESSAGES.CHANGE_LANGUAGE,
      group: LAUNCHER_GROUP.LAUNCHER,
      originTableId: LAUNCHER_ID,
      payload: { lang } as ChangeLanguagePayload,
    };
    this.dispatch(message, tableId, true);
  }

  dispatchGameLaunchedMessage(message: GameLaunchedPayload): void {
    const payload: LauncherPayload = {
      name: LAUNCHER_MESSAGES.GAME_LAUNCHED,
      group: LAUNCHER_GROUP.LAUNCHER,
      originTableId: message.operatorGameId,
    };
    this.dispatch(payload);
  }

  /**
   * Handle incoming messages from the launcher
   */

  handleIncomingMessage(message?: MessageEvent): void {
    if (!this.store || !message) {
      return;
    }

    try {
      /* Paddy Power Event Messages to be forwarded to the game window */
      if (message?.data) {
        let msgdata;
        try {
          msgdata = JSON.parse(message.data);
        } catch (error) {
          msgdata = message.data;
        }
        const iframeContainer = document.getElementsByTagName(`iframe`)[0]?.contentWindow;
        if (isHostToGameRelayPayload(msgdata) && iframeContainer) {
          iframeContainer.postMessage(msgdata, '*');
        }
      }
      const data = message.data as LauncherPayload;
      if (!this.isValidMessage(data)) return;

      const messageOrigin_tableId = data.originTableId;
      switch (data.name) {
        // Game shown acknowledgement
        case LAUNCHER_MESSAGES.GAME_LAUNCHED: {
          const payload = data.payload as GameLaunchedPayload;

          // Update targets
          if (payload) {
            this.setTarget({
              name: `launcher-${messageOrigin_tableId}`,
              type: 'GAME',
              tableId: messageOrigin_tableId,
              window: message.source as Window,
            });

            if (!this.store.showFullscreen) {
              this.store.dispatch(launcherContextActions.gameLoaded());
            }

            // Dispatch items to this new newly launched table
            this.dispatchTablesUpdatedMessage(this.store.tables, messageOrigin_tableId);
            this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo(), messageOrigin_tableId);

            // Set operatorTableId for tableId - need for Launcher Lobby
            const hasOperatorGameId = this.store.tables.some((item) => item.operatorGameId === payload.operatorGameId);
            const hasMTB = this.store.tables.some((table) => table.tableId === 'MTB');
            if (hasMTB && messageOrigin_tableId !== 'MTB') {
              this.store.dispatch(launcherContextActions.changeBaseTable({ tableId: messageOrigin_tableId }));
            }
            if (!hasOperatorGameId) {
              this.store.dispatch(
                launcherContextActions.setOperatorGameId({
                  operatorGameId: payload.operatorGameId,
                  tableId: messageOrigin_tableId,
                })
              );

              // this is a new table, set it as active and mute other table
              // Don't set active table if there is only one table. VolumeRestorer will take over
              if (this.store.tables.length > 1 && !hasMTB) {
                const currentActiveTableId = this.store.tables.filter((item) => item.isActive)?.[0]?.tableId;
                this.store.dispatch(launcherContextActions.setActiveTable({ tableId: messageOrigin_tableId }));
                if (currentActiveTableId) {
                  this.dispatchSetActiveTableMessage(messageOrigin_tableId, currentActiveTableId);
                }
                this.dispatchSetActiveTableMessage(messageOrigin_tableId, messageOrigin_tableId);
              }
            }
            if (data.originTableId === LAUNCHER_GROUP.MTB) {
              this.dispatchGameLaunchedMessage(payload);
            }
          }
          break;
        }

        // Initialise bets info for the newly opened table.
        case LAUNCHER_MESSAGES.INITIALIZE_BETS_INFO: {
          const payload = data.payload as GamesLaunchedPayload;
          BetsInfoController.setNewTable(payload.tableIds);
          break;
        }

        // Add lobby to view
        case LAUNCHER_MESSAGES.OPEN_LAUNCHER_LOBBY: {
          this.store.dispatch(launcherContextActions.openLauncherLobby());
          break;
        }

        // Lobby added to view acknowledgement
        case LAUNCHER_MESSAGES.LOBBY_LOADED: {
          this.store.dispatch(launcherContextActions.lobbyLoaded());
          break;
        }

        // Remove lobby from view
        case LAUNCHER_MESSAGES.LOBBY_CLOSE: {
          this.store.dispatch(launcherContextActions.lobbyClosed());
          break;
        }

        // Table added to view. Message send from lobby
        case LAUNCHER_MESSAGES.GAME_ADD: {
          const payload = data.payload as AddGamePayload;
          const url = new URL(payload.url);
          const language = SessionStorage.getJson<{ lang: string }>(SESSION_KEYS.GAME);
          url.searchParams.set('lang', language?.lang ?? DEFAULT_LANGUAGE_CODE);
          this.store.dispatch(
            launcherContextActions.addTable({
              tableId: payload.tableId,
              url: url.toString(),
              iframeType: IFRAME_TYPE.TABLE,
            })
          );

          // Log event
          const additional = payload.tableId === IFRAME_TYPE.MTB ? IFRAME_TYPE.MTB : IFRAME_TYPE.TABLE;
          Logger.logEvent('launcher', { additional });
          break;
        }

        // Table removed from view
        case LAUNCHER_MESSAGES.GAME_CLOSE: {
          const payload = data.payload as GameClosePayload;

          // remove from store
          this.store.dispatch(launcherContextActions.removeTable({ tableId: payload.tableId }));
          this.store.dispatch(launcherContextActions.toggleMtb({ isMinimize: false }));

          // remove from targets
          this.removeTarget(payload.tableId);

          // remove from betsInfo
          // If MTB is closing, delete all tables except the base table
          if (payload.tableId === IFRAME_TYPE.MTB) {
            const baseTableId = this.store.tables[0]?.tableId;
            if (baseTableId) {
              BetsInfoController.deleteAllTablesExcept([baseTableId]);
            }
          } else {
            BetsInfoController.deleteTable(payload.tableId);
          }
          this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo());
          break;
        }

        // Balance Sync for Multi View
        case LAUNCHER_MESSAGES.BALANCE_UPDATE: {
          const payload = data.payload as BalanceUpdatePayload;

          // Reset winAmount of all tables
          BetsInfoController.resetAmountsAfterBalanceFetch();
          this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo());

          // dispatch balance to all tables except this.
          this.dispatchBalanceUpdateMessage(payload.balance, messageOrigin_tableId, true);
          break;
        }

        // Active Window for controlling which table will be heard
        case LAUNCHER_MESSAGES.SET_ACTIVE_TABLE: {
          const currentActiveTableId = this.store.tables.filter((item) => item.isActive)?.[0]?.tableId;
          if (currentActiveTableId !== messageOrigin_tableId) {
            this.store.dispatch(launcherContextActions.setActiveTable({ tableId: messageOrigin_tableId }));
            if (currentActiveTableId) {
              this.dispatchSetActiveTableMessage(messageOrigin_tableId, currentActiveTableId);
            }
            this.dispatchSetActiveTableMessage(messageOrigin_tableId, messageOrigin_tableId);
          }
          break;
        }

        // Bets Sync
        case LAUNCHER_MESSAGES.BET_PLACED: {
          const payload = data.payload as BetsPlacedPayload;
          BetsInfoController.setBetAmount(payload.tableId, payload.betAmount, payload.betType, payload.bonusId);
          this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo());
          break;
        }

        case LAUNCHER_MESSAGES.BETS_CLOSED: {
          BetsInfoController.setBetClosed(messageOrigin_tableId, true);
          this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo());
          break;
        }

        // Redirect message from game
        case LAUNCHER_MESSAGES.REDIRECT_GAME: {
          const payload = data.payload as RedirectPayload;
          window.location.href = payload.url;
          break;
        }

        case LAUNCHER_MESSAGES.WIN_RESULT: {
          const payload = data.payload as WinResultPayload;
          BetsInfoController.setWinAmount(payload.tableId, payload.winAmount);
          this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo());
          break;
        }

        // Bonus Campaign Sync
        case LAUNCHER_MESSAGES.BONUS_CAMPAIGNS: {
          BetsInfoController.resetBetAmountsAfterBonusCampaignFetch();
          this.dispatchBetsUpdatedMessage(BetsInfoController.getAllBetsInfo());
          this.dispatchBonusCampaignMessage(data.payload as BonusCampaignPayload, messageOrigin_tableId, true);
          break;
        }

        // MTB Messages
        case LAUNCHER_MESSAGES.OPEN_LAUNCHER_MTB: {
          const payload = data.payload as AddMTBPayload;
          const isDesktop = window.innerWidth >= 1024 && window.innerHeight >= 500;
          const url = new URL(payload.url);
          url.searchParams.set('isDesktop', String(isDesktop));
          this.store.dispatch(
            launcherContextActions.openLauncherMTB({
              url: url.toString(),
            })
          );
          break;
        }

        case LAUNCHER_MESSAGES.TOGGLE_MTB: {
          const payload = data.payload as ToggleMtbPayload;
          this.store.dispatch(launcherContextActions.toggleMtb(payload));
          break;
        }

        // Balance Sync for MTB
        case LAUNCHER_MESSAGES.COMPUTED_BALANCE_UPDATE: {
          const payload = data.payload as MtbBalanceUpdatePayload;
          this.dispatchComputedBalanceUpdate(payload.balance);
          break;
        }

        case LAUNCHER_MESSAGES.THEME_CHANGE: {
          const payload = data.payload as ThemeChangePayload;
          this.dispatchThemeChangeMessage(payload.theme);
          break;
        }

        case LAUNCHER_MESSAGES.REPLACE_TABLE: {
          const payload = data.payload as ReplaceTablePayload;
          this.store.dispatch(launcherContextActions.replaceTable(payload));
          break;
        }
        case LAUNCHER_MESSAGES.TOGGLE_NO_COMMISSION: {
          const payload = data.payload as ToggleNoCommissionPayload;
          this.dispatchNoCommissionChangeMessage(payload.isNoCommission);
          break;
        }
        case LAUNCHER_MESSAGES.CHANGE_LANGUAGE: {
          const payload = data.payload as ChangeLanguagePayload;
          this.dispatchChangeLanguageMessage(payload.lang, data.originTableId);
          break;
        }

        default: {
          console.warn('Unhandled message type:', data.name);
        }
      }
    } catch (error) {
      console.error('LauncherCommunicationController: Error parsing message', error);
      return;
    }
  }

  private isValidMessage(data: LauncherPayload): boolean {
    return Boolean(
      data &&
      data.name &&
      (data.group === LAUNCHER_GROUP.LAUNCHER ||
        data.group === LAUNCHER_GROUP.LOBBY ||
        data.group === LAUNCHER_GROUP.MTB)
    );
  }
}

export const LauncherCommunicator = new LauncherCommunicationController();
